import { useState } from 'react';
import { routes } from 'routes/routes';
import { usePermissions } from 'permissions';
import {
  Avatar,
  Badge,
  CommentLineIcon,
  Dropdown,
  ExitLineIcon,
  HeartBeatLineIcon,
  Icon,
  Link,
  Loading,
  MenuLinkItem,
  NotificationLineIcon,
  QuestionLineIcon,
  RocketLineIcon,
  Settings5LineIcon,
  Space,
  User3LineIcon,
  UserAddLineIcon,
  UserSecurityLineIcon,
  message,
} from 'ui';
import {
  generateAdminDashboardUrl,
  routes as routesAdmin,
  useAdminDashboardRedirectMutation,
} from 'features/admin-dashboard';
import { useUser } from 'features/auth';
import { ChangelogModal, useChangelogVisibilityOptions } from 'features/changelog';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { InviteFriendsModal } from 'features/communities/components/invite-friends-modal';
import { generateEhrPortalUrl, routes as routesEhr, useEhrPortalRedirectMutation } from 'features/ehr-portal';

// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { useUnreadMessages } from 'features/users/hooks';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { useNotificationBadgesQuery } from 'features/users/queries';

type P = {
  onLogOut: () => Promise<void>;
};

const UserMenu = ({ onLogOut }: P) => {
  const { data: badges } = useNotificationBadgesQuery();
  const total = badges?.total ?? 0;

  const { isPending: isRedirectingToAdminDashboard, mutate: redirectToAdminDashboard } =
    useAdminDashboardRedirectMutation();
  const { isPending: isRedirectingToEhrPortal, mutate: redirectToEhrPortal } = useEhrPortalRedirectMutation();
  const [isChangelogModalOpened, setIsChangelogModalOpened] = useChangelogVisibilityOptions();
  const [isInviteFriendsModalOpened, setIsInviteFriendsModalOpened] = useState(false);
  const { canViewAdminDashboard, canViewEhrPortal } = usePermissions();

  const { railsUserData: viewer } = useUser();

  const unreadMessages = useUnreadMessages();

  const items = [
    {
      icon: <User3LineIcon />,
      key: 'profile',
      label: <MenuLinkItem text="My Profile" to={routes.members.detail(viewer.id)} />,
      visible: true,
    },
    {
      icon: isRedirectingToAdminDashboard ? <Loading /> : <UserSecurityLineIcon />,
      key: 'adminConsole',
      label: (
        <MenuLinkItem
          onClick={() => {
            message.info('Redirecting to Admin console');
            redirectToAdminDashboard({ referrer: routesAdmin.index });
          }}
          target="_blank"
          text="Admin console"
          to={{ pathname: generateAdminDashboardUrl(routesAdmin.index) }}
        />
      ),
      visible: canViewAdminDashboard({}),
    },
    {
      icon: isRedirectingToEhrPortal ? (
        <Loading indicator={<Icon icon="loading-line" spin />} padding={false} size="small" />
      ) : (
        <HeartBeatLineIcon />
      ),
      key: 'ehrPortal',
      label: (
        <MenuLinkItem
          onClick={() => {
            message.info('Redirecting to EHR Portal');
            redirectToEhrPortal({ referrer: routesEhr.index });
          }}
          target="_blank"
          text="EHR Portal"
          to={{ pathname: generateEhrPortalUrl(routesEhr.index) }}
        />
      ),
      visible: canViewEhrPortal({}),
    },
    {
      icon: <RocketLineIcon />,
      key: 'releaseNotes',
      // TODO this hack with `to=""` is not nice and we should find other solution maybe?
      label: (
        <MenuLinkItem
          onClick={() => {
            setIsChangelogModalOpened(true);
          }}
          text="Release notes"
          to=""
        />
      ),
      visible: true,
    },
    {
      icon: <UserAddLineIcon />,
      key: 'inviteFriendsNew',
      label: (
        <MenuLinkItem
          onClick={() => {
            setIsInviteFriendsModalOpened(true);
          }}
          text="Invite friends"
          // TODO this hack with `to=""` is not nice and we should find other solution maybe?
          to=""
        />
      ),
      visible: true,
    },
    {
      icon: <Settings5LineIcon />,
      key: 'settings',
      label: <MenuLinkItem text="Settings" to={routes.user.settings} />,
      visible: true,
    },
    {
      icon: <QuestionLineIcon />,
      key: 'help',
      label: <MenuLinkItem text="Help" to={routes.user.help} />,
      visible: true,
    },
    {
      icon: <ExitLineIcon />,
      key: 'logout',
      label: <MenuLinkItem onClick={onLogOut} text="Log out" to="" />,
      visible: true,
    },
  ]
    .filter((item) => item.visible)
    .map(({ visible, ...item }) => ({ ...item, className: 'user-menu__dropdown__item' }));

  return (
    <>
      <Space className="user-menu" size="ms">
        <Link to={routes.notifications}>
          <Badge className="user-menu__badge" color="red" count={Math.max(total, 0)} overflowCount={99}>
            <NotificationLineIcon className="user-menu__icon" size="middle" />
          </Badge>
        </Link>
        <Link to={routes.messages}>
          <Badge className="user-menu__badge" color="red" count={Math.max(unreadMessages, 0)} overflowCount={99}>
            <CommentLineIcon className="user-menu__icon" size="middle" />
          </Badge>
        </Link>
        <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
          <Avatar className="user-menu__dropdown__avatar" photo={viewer.profilePhoto?.url} />
        </Dropdown>
      </Space>

      <ChangelogModal
        isOpened={isChangelogModalOpened}
        onClose={() => {
          setIsChangelogModalOpened(false);
        }}
      />
      <InviteFriendsModal
        isOpened={isInviteFriendsModalOpened}
        onCancel={() => {
          setIsInviteFriendsModalOpened(false);
        }}
      />
    </>
  );
};

export default UserMenu;
